import Clarity from '@microsoft/clarity';
import { useEffect, useState } from 'react';
import { api } from './axios';

export const TOKEN_KEY = '@gopass-token';
export const TOKEN_KEY_USER = '@gopass-user';
const USER_UPDATED_EVENT = 'gopass-user-updated';
export const MENSAGEM_PENDENCIA_FINANCEIRA_NOVA_VENDA = "Existem pendências financeiras que impedem novas vendas. Por favor regularize os pagamentos para liberar novas vendas. Em caso de dúvida entre em contato com o suporte.";
export const MENSAGEM_PENDENCIA_FINANCEIRA_TOPO = "Existem pendências financeiras que impedem novas vendas. Por favor, regularize as faturas pendentes no menu Financeiro para efetuar novas vendas. Em caso de dúvidas, entre em contato com o suporte.";

export const isAuthenticated = () => localStorage.getItem(TOKEN_KEY) !== null;

export const getToken = () => localStorage.getItem(TOKEN_KEY);

type AgenciaPayload = {
  id?: string | number;
  nome?: string;
  nome_fantasia?: string;
  status?: number | string;
  pendencia_financeira?: boolean;
  exibir_provedor?: boolean | number | string;
  exibirProvedor?: boolean | number | string;
  [key: string]: unknown;
};

export type UserPayload = {
  id: string;
  name: string;
  agencia?: AgenciaPayload | null;
  agencias?: AgenciaPayload[];
  exibir_provedor?: boolean | number | string;
  exibirProvedor?: boolean | number | string;
  description?: string;
  tipo?: number | { value?: number | string; name?: string; label?: string } | string;
  [key: string]: unknown;
};

export const login = (token: string, user: UserPayload) => {
  Clarity.identify(user.id, token, "login", user.name)
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(TOKEN_KEY_USER, JSON.stringify(user));
};

export const getUser = () => {
  const storedUser = localStorage.getItem(TOKEN_KEY_USER);

  if (!storedUser) {
    return;
  }

  try {
    return JSON.parse(storedUser);
  } catch {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_KEY_USER);
    return;
  }
};

export const updateUser = (user: Partial<UserPayload>) => {
  const storedUser = getUser();
  const updatedUser = {
    ...storedUser,
    ...user,
    agencia: {
      ...(storedUser?.agencia ?? {}),
      ...(user.agencia ?? {}),
    },
  };

  localStorage.setItem(TOKEN_KEY_USER, JSON.stringify(updatedUser));
  window.dispatchEvent(new Event(USER_UPDATED_EVENT));
};

let refreshUserRequest: Promise<UserPayload | undefined> | null = null;

export const refreshUser = async () => {
  if (!getToken()) {
    return undefined;
  }

  if (!refreshUserRequest) {
    refreshUserRequest = api.get('/perfil', {
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
        Expires: "0",
      },
      params: {
        refresh: Date.now(),
      },
    })
      .then(({ data }) => {
        const user = data?.user as UserPayload | undefined;

        if (user) {
          updateUser(user);
        }

        return user;
      })
      .finally(() => {
        refreshUserRequest = null;
      });
  }

  return refreshUserRequest;
};

const isEnabled = (value: unknown) => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value === 1;
  }

  if (typeof value === "string") {
    return ["1", "true", "sim", "yes"].includes(value.trim().toLowerCase());
  }

  return false;
};

export const temPendenciaFinanceira = () => {
  const user = getUser();

  return Boolean(user?.agencia?.pendencia_financeira || Number(user?.agencia?.status) === 3);
};

export const vendeViagemPremiada = () => {
  const user = getUser();

  if(!user){
    return false;
  }

  if(user?.viagem_premiada !== undefined){
    return Boolean(user.viagem_premiada);
  }

  if((user as any)?.agencia?.viagem_premiada !== undefined){
    return Boolean((user as any).agencia.viagem_premiada);
  }

  if(user?.seguro !== undefined){
    return Boolean(user.seguro);
  }

  return false;
};

export const vendeSeguro = vendeViagemPremiada;

export const shouldShowProvider = () => {
  const user = getUser();
  const agenciaId = user?.agencia?.id !== undefined ? String(user.agencia.id) : undefined;
  const agencias = user?.agencias as AgenciaPayload[] | undefined;
  const agenciaCompleta = agenciaId
    ? agencias?.find((agencia) => agencia?.id !== undefined && String(agencia.id) === agenciaId)
    : undefined;

  return isEnabled(user?.agencia?.exibir_provedor)
    || isEnabled(user?.agencia?.exibirProvedor)
    || isEnabled(agenciaCompleta?.exibir_provedor)
    || isEnabled(agenciaCompleta?.exibirProvedor)
    || isEnabled(user?.exibir_provedor)
    || isEnabled(user?.exibirProvedor);
};

export const useShouldShowProvider = () => {
  const [showProvider, setShowProvider] = useState(shouldShowProvider);

  useEffect(() => {
    const update = () => setShowProvider(shouldShowProvider());

    window.addEventListener(USER_UPDATED_EVENT, update);
    window.addEventListener('storage', update);

    return () => {
      window.removeEventListener(USER_UPDATED_EVENT, update);
      window.removeEventListener('storage', update);
    };
  }, []);

  return showProvider;
};

export const logout = async () => {
  await api.post('/logout');
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_KEY_USER);
};

export const logoutRemote = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_KEY_USER);

  window.location.href = '/login'
}

export const canPermission = (role: string) => {
  const user = getUser();
  if (user.roles !== undefined) {
    const roles = Object.values(user.roles);

    if (roles.find((rolePermission) => rolePermission === role) || user.admin) return true;

    return false;
  }

  return false;
};

export const isRepresentante = () => {
  const user = getUser();
  if (!user) {
    return false;
  }

  const normalize = (value: unknown) =>
    typeof value === 'string' ? value.toLowerCase() : '';

  const tipo = user.tipo;
  if (typeof tipo === 'number') {
    return tipo === 4;
  }

  if (typeof tipo === 'string') {
    if (tipo.toLowerCase() === 'representante') {
      return true;
    }

    const numericTipo = Number(tipo);
    return Number.isFinite(numericTipo) && numericTipo === 4;
  }

  if (tipo && typeof tipo === 'object' && 'value' in tipo) {
    const { value, name, label } = tipo as { value?: number | string; name?: string; label?: string };
    const numericValue = Number(value);
    if (Number.isFinite(numericValue) && numericValue === 4) {
      return true;
    }

    return normalize(name) === 'representante' || normalize(label) === 'representante';
  }

  if (normalize(user.perfil) === 'representante' || normalize(user.description) === 'representante') {
    return true;
  }

  return false;
};

export const isEmissor = () => shouldHideFeeDetails();

export const shouldHideFeeDetails = () => {
  const user = getUser();

  if (!user) {
    return false;
  }

  const normalize = (value: unknown) =>
    typeof value === 'string' ? value.toLowerCase() : '';

  const tipo = user.tipo;
  if (typeof tipo === 'number') {
    return tipo === 5;
  }

  if (typeof tipo === 'string') {
    if (tipo.toLowerCase() === 'emissor') {
      return true;
    }

    const numericTipo = Number(tipo);
    return Number.isFinite(numericTipo) && numericTipo === 5;
  }

  if (tipo && typeof tipo === 'object' && 'value' in tipo) {
    const { value, name, label } = tipo as { value?: number | string; name?: string; label?: string };
    const numericValue = Number(value);
    if (Number.isFinite(numericValue) && numericValue === 5) {
      return true;
    }

    return normalize(name) === 'emissor' || normalize(label) === 'emissor';
  }

  return normalize(user.perfil) === 'emissor' || normalize(user.description) === 'emissor';
};
